/**
 * @fileoverview Interactive name wheel component for randomly selecting names from a list.
 * 
 * @component NameGenerator
 * @description A spinning wheel interface that allows users to add names and randomly select one.
 * Features animated spinning, name management, and drag-and-drop reordering.
 * 
 * @typedef {Object} SpinData
 * @property {number} targetDegrees - Final rotation angle in degrees
 * @property {number} spinRevolutions - Number of full rotations before stopping
 * 
 * Features:
 * - Interactive spinning wheel animation
 * - Name list management (add, remove, edit)
 * - Drag-and-drop name reordering
 * - Persistent name list storage
 * - Theme-aware styling
 * - Guided tour integration
 * - Right-side panel toggle
 * 
 * State Management:
 * - Spinning animation state
 * - Name list data
 * - Selected name tracking
 * - UI visibility states
 */

import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
} from "react";
import axios, { AxiosError } from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import NameWheel from "../../components/NameWheel";
import WheelSegment from "../../components/WheelSegment";
import { useTour } from "../../context/TourContext";
import { tourStepsNameGenerator } from "./tourStepsNameGenerator";
import GuidedTour from "../../components/GuidedTour";
import { VisibilityStates } from "../../types/VisibilityStates";
import {
  DEFAULT_CUSTOM_WHEEL_COLORS,
  WHEEL_PALETTES,
  WheelPaletteId,
  isWheelPaletteId,
} from "../../constants/wheelPalettes";
import { unlockWheelAudio } from "../../utils/wheelSounds";
import Confetti from "react-confetti";

const defaultColorForIndex = (index: number) =>
  DEFAULT_CUSTOM_WHEEL_COLORS[index % DEFAULT_CUSTOM_WHEEL_COLORS.length];

const persistCustomColors = (colors: string[]) => {
  localStorage.setItem("spinnerCustomColors", JSON.stringify(colors));
  return colors;
};

const addOffWheelIndex = (
  currentOff: number[],
  index: number,
  totalNames: number,
): number[] => {
  if (currentOff.includes(index)) {
    return currentOff;
  }
  const next = [...currentOff, index];
  return next.length >= totalNames ? [] : next;
};

const remapIndexesAfterMove = (
  indexes: number[],
  from: number,
  to: number,
): number[] => {
  return indexes.map((index) => {
    if (index === from) return to;
    if (from < to && index > from && index <= to) return index - 1;
    if (from > to && index >= to && index < from) return index + 1;
    return index;
  });
};

const NameGenerator: React.FC = () => {
  const [nameInput, setNameInput] = useState<string>("");
  const [nameList, setNameList] = useState<string[]>([]);
  const [offWheelIndexes, setOffWheelIndexes] = useState<number[]>([]);
  const [pendingOffIndex, setPendingOffIndex] = useState<number | null>(null);
  const [removeAfterSpin, setRemoveAfterSpin] = useState<boolean>(() => {
    return localStorage.getItem("spinnerRemoveAfterSpin") === "true";
  });
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => {
    return localStorage.getItem("spinnerSoundEnabled") !== "false";
  });
  const [wheelPaletteId, setWheelPaletteId] = useState<WheelPaletteId>(() => {
    const saved = localStorage.getItem("spinnerWheelPalette");
    return saved && isWheelPaletteId(saved) ? saved : "rainbow";
  });
  const [customWheelColors, setCustomWheelColors] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem("spinnerCustomColors");
      if (!saved) return DEFAULT_CUSTOM_WHEEL_COLORS;
      const parsed = JSON.parse(saved);
      if (
        Array.isArray(parsed) &&
        parsed.every((color) => typeof color === "string")
      ) {
        const validColors = parsed.filter((color) =>
          /^#([0-9a-fA-F]{6})$/.test(color),
        );
        if (validColors.length > 0) {
          return validColors;
        }
      }
    } catch (error) {
      console.error("Error loading custom wheel colors:", error);
    }
    return DEFAULT_CUSTOM_WHEEL_COLORS;
  });
  const [nameListId, setNameListId] = useState<number | null>(null);
  const [hasLoadedNames, setHasLoadedNames] = useState(false);
  const [isSpinning, setIsSpinning] = useState<boolean>(false);
  const [showRightSide, setShowRightSide] = useState<boolean>(false);
  const { getAccessTokenSilently } = useAuth0();
  const containerRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const { isTourRunning: isContextTourRunning } = useTour();
  const [spinData, setSpinData] = useState<{
    targetDegrees: number;
    spinRevolutions: number;
  } | undefined>(undefined);

  // Initialize visibilityStates with all properties
  const [visibilityStates, setVisibilityStates] = useState<VisibilityStates>({
    isNameInputVisible: true,
    isAddNameButtonVisible: true,
    isSpinButtonVisible: true,
    isNamesListVisible: true,
    isDotCountTypeVisible: false,
    isMinDotsVisible: false,
    isMaxDotsVisible: false,
    isTypeSelectVisible: false,
    isItemCountVisible: false,
    isCollectionItemCountVisible: false,
    isDotColorVisible: false,
    isDotShapeVisible: false,
    isGenerateRandomSequenceButtonVisible: false,
    isFileUploadVisible: false,
    isClearButtonVisible: false,
    isGeneratedSequencePreviewVisible: false,
    isSearchInputVisible: false,
    isSortSelectVisible: false,
    isCollectionsGridVisible: false,
    isPreviewButtonVisible: false,
    isSaveButtonVisible: false,
    isItemPreviewVisible: false,
    isMathProblemVisible: false,
    isDotButtonVisible: false,
    isImageUploadVisible: false,
    isCollectionNameVisible: false,
    isCategorySelectVisible: false,
    isStageSelectVisible: false,
    isPublicCheckboxVisible: false,
    isSubmitButtonVisible: false,
    isCollectionCardVisible: false,
    isStartCollectionButtonVisible: false,
    isEditCollectionButtonVisible: false,
    isDeleteCollectionButtonVisible: false,
    isSessionSettingsModalVisible: false,
    isEditCollectionModalVisible: false,
    isDuplicateCollectionModalVisible: false,
    isCollectionPreviewModalVisible: false,
    isNextButtonVisible: false,
  });

  const [isTourRunning, setIsTourRunning] = useState<boolean>(false);
  const [currentTourStep, setCurrentTourStep] = useState<number>(0);

  // Define the steps variable
  const steps = tourStepsNameGenerator(visibilityStates); // Create tour steps based on visibility states

  // Add a function to start the tour
  const startTour = () => {
    const tourCompleted = localStorage.getItem("tourCompleted");
    if (!tourCompleted) {
      setShowRightSide(true);
      window.setTimeout(() => {
        setIsTourRunning(true);
        setCurrentTourStep(0);
      }, 350);
    }
  };

  // Start the tour when the component mounts
  useEffect(() => {
    startTour(); // Call the startTour function
  }, []);

  const handleTourComplete = () => {
    console.log("Tour completed");
    setIsTourRunning(false); // Reset the tour running state
  };

  const handleTourStepChange = (step: number) => {
    setCurrentTourStep(step);
  };

  const API_URL =
    import.meta.env.VITE_API_BASE_URL ||
    "https://race-the-clock-backend-production.up.railway.app";

  const loadNameList = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get(`${API_URL}/namelists/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("Name list loaded successfully");
      if (response.data.length > 0) {
        const latestNameList = response.data[0];
        setNameListId(latestNameList.namelist_id);
        setNameList(latestNameList.names);
      } else {
        console.warn("No name lists found.");
      }
    } catch (error) {
      console.error("Error loading name list:", error);
    } finally {
      setHasLoadedNames(true);
    }
  }, [getAccessTokenSilently, API_URL]); // Added API_URL to the dependency array

  useEffect(() => {
    loadNameList();
  }, [loadNameList]);

  useEffect(() => {
    if (!hasLoadedNames || nameList.length === 0) {
      return;
    }

    setCustomWheelColors((prev) => {
      if (prev.length === nameList.length) {
        return prev;
      }
      const next =
        prev.length < nameList.length
          ? [
              ...prev,
              ...Array.from(
                { length: nameList.length - prev.length },
                (_, index) => defaultColorForIndex(prev.length + index),
              ),
            ]
          : prev.slice(0, nameList.length);
      return persistCustomColors(next);
    });
  }, [nameList.length, hasLoadedNames]);

  const saveNameList = async (updatedList: string[] = nameList) => {
    try {
      console.log("Saving name list...");
      const token = await getAccessTokenSilently();
      const data = { name: "My Name List", names: updatedList };

      if (nameListId) {
        console.log("Updating existing list");
        await axios.put(`${API_URL}/namelists/${nameListId}`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("List updated successfully");
      } else {
        console.log("Creating new list");
        const response = await axios.post(`${API_URL}/namelists/`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("List created successfully");
        // We need this response to get the new nameListId
        setNameListId(response.data.namelist_id);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error("API Error:", error.message);
      } else {
        console.error("Error saving name list", error);
      }
    }
  };

  const handleAddName = () => {
    if (nameInput.trim() !== "") {
      const updatedList = [...nameList, nameInput.trim()];
      setNameList(updatedList);
      setNameInput("");
      saveNameList(updatedList);
      setCustomWheelColors((prev) =>
        persistCustomColors([...prev, defaultColorForIndex(prev.length)]),
      );

      // Update visibility states if needed
      setVisibilityStates((prev) => ({
        ...prev,
        isNamesListVisible: true, // Show names list after adding a name
      }));
    }
  };

  const handleRemoveName = (index: number) => {
    const updatedList = nameList.filter((_, i) => i !== index);
    setNameList(updatedList);
    saveNameList(updatedList);
    setOffWheelIndexes((prev) =>
      prev
        .filter((offIndex) => offIndex !== index)
        .map((offIndex) => (offIndex > index ? offIndex - 1 : offIndex)),
    );
    setPendingOffIndex((pending) => {
      if (pending === null) return null;
      if (pending === index) return null;
      return pending > index ? pending - 1 : pending;
    });
    setCustomWheelColors((prev) =>
      persistCustomColors(prev.filter((_, colorIndex) => colorIndex !== index)),
    );
  };

  const handleEditName = (index: number, newName: string) => {
    const updatedList = [...nameList];
    updatedList[index] = newName.trim();
    setNameList(updatedList);
    saveNameList(updatedList);
  };

  const handleToggleNameOnWheel = (index: number) => {
    if (isSpinning) return;
    if (pendingOffIndex === index) {
      setPendingOffIndex(null);
      return;
    }
    setOffWheelIndexes((prev) =>
      prev.includes(index)
        ? prev.filter((offIndex) => offIndex !== index)
        : [...prev, index],
    );
  };

  const namesOnWheel = nameList.filter(
    (_, index) => !offWheelIndexes.includes(index),
  );
  const customColorsOnWheel = nameList.flatMap((_, index) =>
    offWheelIndexes.includes(index)
      ? []
      : [customWheelColors[index] ?? defaultColorForIndex(index)],
  );

  const takePendingNameOffWheel = (currentOff: number[]) => {
    if (pendingOffIndex === null) {
      return currentOff;
    }
    return addOffWheelIndex(currentOff, pendingOffIndex, nameList.length);
  };

  const handleSpin = () => {
    if (isSpinning) {
      return;
    }

    const nextOff = takePendingNameOffWheel(offWheelIndexes);
    const wheelNames = nameList.filter((_, index) => !nextOff.includes(index));
    if (wheelNames.length === 0) {
      return;
    }

    if (pendingOffIndex !== null) {
      setOffWheelIndexes(nextOff);
      setPendingOffIndex(null);
    }

    void unlockWheelAudio();
    setIsSpinning(true);
    setShowConfetti(false);
    const spinRevolutions = 5 + Math.random() * 3;
    const degreesPerSlice = 360 / wheelNames.length;
    const selectedIndex = Math.floor(Math.random() * wheelNames.length);
    const targetDegrees =
      selectedIndex * degreesPerSlice + degreesPerSlice / 2;
    const totalDegrees = spinRevolutions * 360 + targetDegrees;

    setSpinData({ targetDegrees: totalDegrees, spinRevolutions });
  };

  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const handleNameSelected = (name: string) => {
    console.log("Name selected");
    setIsSpinning(false);
    setSelectedName(name);
    setShowConfetti(true);

    if (!removeAfterSpin) return;

    const selectedIndex = nameList.findIndex(
      (listName, index) =>
        listName === name && !offWheelIndexes.includes(index),
    );
    if (selectedIndex !== -1) {
      setPendingOffIndex(selectedIndex);
    }
  };

  const handleToggleRemoveAfterSpin = () => {
    setRemoveAfterSpin((prev) => {
      const next = !prev;
      localStorage.setItem("spinnerRemoveAfterSpin", String(next));
      return next;
    });
  };

  const handleToggleSound = () => {
    setSoundEnabled((prev) => {
      const next = !prev;
      localStorage.setItem("spinnerSoundEnabled", String(next));
      return next;
    });
  };

  const handlePaletteChange = (paletteId: WheelPaletteId) => {
    setWheelPaletteId(paletteId);
    localStorage.setItem("spinnerWheelPalette", paletteId);
  };

  const handleCustomColorChange = (index: number, color: string) => {
    setCustomWheelColors((prev) => {
      const next = [...prev];
      next[index] = color;
      return persistCustomColors(next);
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleAddName();
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleToggleRightSide = () => {
    setShowRightSide(!showRightSide);
  };

  const openNamePanelForAdd = () => {
    if (isSpinning) {
      return;
    }
    setShowRightSide(true);
    window.setTimeout(() => {
      nameInputRef.current?.focus();
    }, 150);
  };

  useLayoutEffect(() => {
    if (isContextTourRunning) {
      setShowRightSide(true);
    }
  }, [isContextTourRunning]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isContextTourRunning || isTourRunning) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target?.closest(".start-tour-button")) {
        return;
      }
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowRightSide(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isContextTourRunning, isTourRunning]);

  const handleDragStart = (
    event: React.DragEvent<HTMLLIElement>,
    index: number,
  ) => {
    event.dataTransfer.setData("text/plain", index.toString());
  };

  const handleDragOver = (event: React.DragEvent<HTMLLIElement>) => {
    event.preventDefault(); // Prevent default to allow drop
  };

  const handleDrop = (event: React.DragEvent<HTMLLIElement>, index: number) => {
    const draggedIndex = Number(event.dataTransfer.getData("text/plain"));
    if (draggedIndex !== index) {
      const updatedList = [...nameList];
      const [movedItem] = updatedList.splice(draggedIndex, 1); // Remove the dragged item
      updatedList.splice(index, 0, movedItem); // Insert it at the new position
      setNameList(updatedList);
      saveNameList(updatedList);
      setOffWheelIndexes((prev) =>
        remapIndexesAfterMove(prev, draggedIndex, index),
      );
      setPendingOffIndex((pending) => {
        if (pending === null) return null;
        return remapIndexesAfterMove([pending], draggedIndex, index)[0];
      });
      setCustomWheelColors((prev) => {
        const next = [...prev];
        const [movedColor] = next.splice(draggedIndex, 1);
        next.splice(index, 0, movedColor);
        return persistCustomColors(next);
      });
    }
  };

  return (
    <div className="page-container">
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={400}
          onConfettiComplete={() => {
            setShowConfetti(false);
            if (pendingOffIndex === null) {
              return;
            }
            setOffWheelIndexes((prev) =>
              addOffWheelIndex(prev, pendingOffIndex, nameList.length),
            );
            setPendingOffIndex(null);
          }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 100,
            pointerEvents: "none",
          }}
        />
      )}
      <div
        ref={containerRef}
        className={`name-generator relative text-theme-text`}
      >
        <div
          className={`flex w-full max-w-6xl flex-col items-center px-2 lg:flex-row lg:items-start ${
            showRightSide ? "gap-8 lg:justify-between" : "justify-center"
          }`}
        >
          {/* Left side */}
          <div
            className={`flex flex-col items-center ${showRightSide ? "w-full lg:w-1/2" : "w-full"}`}
          >
            {/* Selected name display - moved above the wheel */}
            {selectedName && (
              <div className="mb-4 text-center">
                <h2 className="text-2xl font-bold">Selected Name:</h2>
                <p className="text-3xl font-bold text-blue-600">
                  {selectedName}
                </p>
              </div>
            )}

            <div className="relative flex w-full flex-col items-center">
              <NameWheel
                names={namesOnWheel}
                isSpinning={isSpinning}
                spinData={spinData}
                onNameSelected={handleNameSelected}
                paletteId={wheelPaletteId}
                customColors={customColorsOnWheel}
                soundEnabled={soundEnabled}
                onAddNameClick={openNamePanelForAdd}
                stopSpinning={() => {
                  setIsSpinning(false);
                  setSpinData(undefined);
                }}
              />

              {visibilityStates.isSpinButtonVisible && (
                <button
                  type="button"
                  onClick={handleSpin}
                  disabled={namesOnWheel.length === 0}
                  className={`spin-button bg-light-blue hover:bg-hover-blue active:bg-active-blue relative z-10 min-h-[44px] w-full max-w-md rounded border border-black px-4 py-3 font-bold uppercase text-black transition duration-300 hover:scale-105 active:scale-95 ${
                    namesOnWheel.length === 0
                      ? "cursor-not-allowed opacity-50"
                      : ""
                  }`}
                >
                  Spin the Wheel
                </button>
              )}
              <label className="mt-3 flex items-center">
                <input
                  type="checkbox"
                  checked={removeAfterSpin}
                  onChange={handleToggleRemoveAfterSpin}
                  disabled={isSpinning}
                  className="mr-2"
                />
                Take name off the wheel after it's picked
              </label>
              <label className="mt-3 flex items-center">
                <input
                  type="checkbox"
                  checked={soundEnabled}
                  onChange={handleToggleSound}
                  className="mr-2"
                />
                Wheel sound
              </label>
              {(offWheelIndexes.length > 0 || pendingOffIndex !== null) && (
                <button
                  type="button"
                  onClick={() => {
                    setOffWheelIndexes([]);
                    setPendingOffIndex(null);
                  }}
                  className="mt-2 min-h-[44px] rounded border border-black bg-yellow-500 px-4 py-2 font-bold uppercase text-black transition duration-300 hover:bg-yellow-600"
                >
                  Put all names back
                </button>
              )}
              <label className="slice-colors mt-4 flex items-center gap-2">
                <span>Slice colors:</span>
                <select
                  title="Choose wheel slice colors"
                  value={wheelPaletteId}
                  onChange={(e) =>
                    handlePaletteChange(e.target.value as WheelPaletteId)
                  }
                  className="rounded border border-black bg-white p-1 text-black"
                >
                  {WHEEL_PALETTES.map((palette) => (
                    <option key={palette.id} value={palette.id}>
                      {palette.label}
                    </option>
                  ))}
                </select>
              </label>
              {wheelPaletteId === "custom" && (
                <div className="mt-2 flex w-full max-w-md flex-col items-center gap-2">
                  {nameList.length === 0 ? (
                    <p className="text-center text-sm">
                      Add names to choose a color for each slice.
                    </p>
                  ) : (
                    <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
                      {nameList.map((name, index) => (
                        <label
                          key={`${name}-${index}`}
                          className="flex items-center gap-2"
                        >
                          <input
                            type="color"
                            value={
                              customWheelColors[index] ??
                              defaultColorForIndex(index)
                            }
                            onChange={(e) =>
                              handleCustomColorChange(index, e.target.value)
                            }
                            title={`Color for ${name}`}
                            className="size-10 shrink-0 cursor-pointer rounded border border-black sm:size-8"
                          />
                          <span className="truncate">{name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right side (hidden by default, shown when triggered) */}
          <div
            className={`flex w-full flex-col transition-all duration-300 ${
              showRightSide
                ? "max-w-md opacity-100 lg:w-1/2 lg:max-w-none lg:pr-8"
                : "h-0 w-0 overflow-hidden opacity-0"
            }`}
          >
            {visibilityStates.isNameInputVisible && (
              <div className="mb-5 flex w-full flex-col items-center">
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <label htmlFor="nameInput" className="mr-2 whitespace-nowrap">
                    Add a Name:
                  </label>
                  <input
                    ref={nameInputRef}
                    type="text"
                    id="nameInput"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className={`font-teacher mr-2 rounded border border-black bg-white p-2 text-center text-base text-black`}
                    placeholder="Enter a name"
                  />
                  {visibilityStates.isAddNameButtonVisible && (
                    <button
                      type="button"
                      onClick={handleAddName}
                      className="add-name-button bg-light-blue hover:bg-hover-blue active:bg-active-blue min-h-[44px] rounded border border-black px-4 py-2 font-bold uppercase text-black transition duration-300 hover:scale-105 active:scale-95"
                    >
                      Add
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Right side */}
            <div
              className={`flex w-full flex-col ${
                showRightSide ? "opacity-100" : "hidden"
              }`}
            >
              {/* Names list */}
              {visibilityStates.isNamesListVisible && (
                <div className="flex w-full flex-col items-center">
                  <h2 className="text-l inherit mb-2 font-bold">
                    Saved Names:
                  </h2>
                  <p className="inherit mb-2 text-sm">
                    Uncheck a name to take it off the wheel, or check it to put
                    it back.
                  </p>
                  <ul className="inherit w-full max-w-md list-none p-0 text-center">
                    {nameList.map((name, index) => (
                      <WheelSegment
                        key={index}
                        name={name}
                        index={index}
                        angle={0}
                        radius={150}
                        isPicked={
                          offWheelIndexes.includes(index) ||
                          pendingOffIndex === index
                        }
                        onToggleOnWheel={() => handleToggleNameOnWheel(index)}
                        isToggleDisabled={isSpinning}
                        onRemove={() => handleRemoveName(index)}
                        onEdit={(newName) => handleEditName(index, newName)}
                        onDragStart={(event) =>
                          handleDragStart(
                            event as React.DragEvent<HTMLLIElement>,
                            index,
                          )
                        }
                        onDragOver={(event) =>
                          handleDragOver(
                            event as React.DragEvent<HTMLLIElement>,
                          )
                        }
                        onDrop={(event) =>
                          handleDrop(
                            event as React.DragEvent<HTMLLIElement>,
                            index,
                          )
                        }
                      />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Toggle button (always visible) */}
          <button
            type="button"
            className="absolute right-2 top-2 z-20 flex size-11 items-center justify-center rounded-full border border-black bg-green-500 text-2xl text-white transition duration-300 hover:bg-green-600 lg:right-4 lg:top-4"
            onClick={handleToggleRightSide}
            title={
              showRightSide ? "Hide name input panel" : "Show name input panel"
            }
          >
            <div className="relative flex items-center">
              {!showRightSide && (
                <span className="absolute right-12 animate-pulse whitespace-nowrap rounded border border-gray-300 bg-white px-2 py-1 text-sm text-blue-500">
                  Click to add names →
                </span>
              )}
              {showRightSide ? "-" : "+"}
            </div>
          </button>

          {/* Add the GuidedTour component here */}
          <GuidedTour
            steps={steps}
            isRunning={isTourRunning}
            onComplete={handleTourComplete}
            currentStep={currentTourStep}
            onStepChange={handleTourStepChange}
            tourName="nameGenerator"
          />
        </div>
      </div>
    </div>
  );
};

export default NameGenerator;
